import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { api } from './api';
import { useAuth } from './AuthContext';
import { Building2, Plus, LogOut, Loader2, User as UserIcon } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import DialogShell from './kit/DialogShell';
import { BRAND } from './kit/tokens';
import PhoneInput from './shared/PhoneInput';
import MoneyInput from './shared/MoneyInput';
import AddressFields from './shared/AddressFields';
import { emptyAddress, addressPayload, addressLine } from './shared/address';
import { LOAN_TYPE_LABELS, optionsFrom } from './shared/labels';
import { formatDate } from './kit/time';
import { errorMessage } from './errors';
import MyDealConversations from './MyDealConversations';

const stageColors = { new: '#94a3b8', contacted: '#3b82f6', qualified: '#8b5cf6', application: '#f59e0b', processing: '#f97316', approved: BRAND.accentBright, closed_won: '#22c55e', closed_lost: '#ef4444' };
const stageLabels = { new: 'New', contacted: 'Contacted', qualified: 'Qualified', application: 'Application', processing: 'Processing', approved: 'Approved', closed_won: 'Closed Won', closed_lost: 'Lost' };
const FALLBACK_STAGE_COLOR = '#94a3b8';
const stageLabel = (s) => stageLabels[s] || String(s || 'unknown').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const empty = { buyer_name: '', buyer_email: '', buyer_phone: '', loan_type: 'purchase', estimated_amount: '', property_address: '', property_address_parts: emptyAddress(), notes: '' };

export default function RealtorPortalPage() {
  const { user, loading, logout } = useAuth();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get('/realtor/pipeline');
      setList(data.leads || []);
      setLoadError(null);
    } catch (e) {
      // A failed load must never read as an empty pipeline.
      setLoadError(errorMessage(e, 'Try again in a minute.'));
    }
  };
  useEffect(() => { if (user?.role === 'realtor') load(); }, [user]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-brand-accent" /></div>;
  if (!user) return <Navigate to="/portal/login" replace />;
  if (user.role !== 'realtor') return <Navigate to="/portal" replace />;

  const doLogout = async () => { await logout(); navigate('/portal/login'); };

  const submit = async () => {
    if (!form.buyer_name) { toast({ title: 'Buyer name required' }); return; }
    setBusy(true);
    try {
      const parts = addressPayload(form.property_address_parts);
      await api.post('/realtor/refer', {
        ...form,
        estimated_amount: form.estimated_amount === '' ? null : form.estimated_amount,
        property_address_parts: parts,
        property_address: addressLine(parts),
      });
      toast({ title: 'Referral sent!', description: 'Our loan team will contact your buyer within the hour.' });
      setForm(empty); setOpen(false); load();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail || 'Try again' }); }
    setBusy(false);
  };

  const byStage = {};
  list.forEach(l => { (byStage[l.status] = byStage[l.status] || []).push(l); });

  return (
    <div className="min-h-screen" style={{ background: '#F5F6FA' }}>
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/assets/tcl-logo.png" alt="Top Choice Lending" className="h-10 w-auto" />
            <div className="leading-tight hidden md:block">
              <div className="text-xs uppercase tracking-widest text-brand-accent">Realtor Partner Portal</div>
              <div className="font-serif font-semibold text-brand-navy">{user.name}</div>
            </div>
          </div>
          <button onClick={doLogout} className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"><LogOut size={14} /> Sign out</button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4 md:p-8">
        <MyDealConversations />
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-serif text-3xl font-bold text-brand-navy">Your Buyer Pipeline</h1>
            {!(loadError && list.length === 0) && (
              <p className="text-slate-500 mt-1">{list.length} client{list.length !== 1 ? 's' : ''} in the pipeline.</p>
            )}
          </div>
          <Button variant="accent" onClick={() => setOpen(true)}><Plus size={16} /> Refer a Buyer</Button>
        </div>

        {loadError && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 text-red-800 text-sm px-4 py-3 flex items-center justify-between gap-3 flex-wrap" role="alert" data-testid="pipeline-load-error">
            <span>We could not load your pipeline. {loadError}</span>
            <Button variant="outline" onClick={load} data-testid="pipeline-retry">Retry</Button>
          </div>
        )}

        {list.length === 0 && loadError ? null : list.length === 0 ? (
          <div className="bg-white rounded-2xl p-16 text-center border border-slate-100 shadow-sm">
            <Building2 size={40} className="mx-auto text-slate-300 mb-3" />
            <div className="font-serif text-xl font-bold text-brand-navy">Refer your first buyer</div>
            <p className="text-slate-500 mt-2">Send us your prospects — we'll pre-qualify them fast and keep you in the loop every step.</p>
            <Button variant="accent" className="mt-6" onClick={() => setOpen(true)}><Plus size={16} /> Refer a Buyer</Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {list.map(l => (
              <div key={l.lead_id} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-full flex items-center justify-center font-semibold bg-brand-navy" style={{ color: BRAND.accentSoft }}>{l.name?.[0]?.toUpperCase()}</div>
                  <div>
                    <div className="font-semibold text-brand-navy">{l.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">Referred {formatDate(l.created_at) || '—'} • Updated {formatDate(l.updated_at) || '—'}</div>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <span className="text-xs px-3 py-1.5 rounded-full font-semibold uppercase tracking-wider" style={{ background: (stageColors[l.status] || FALLBACK_STAGE_COLOR) + '20', color: stageColors[l.status] || FALLBACK_STAGE_COLOR }}>
                    {stageLabel(l.status)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <DialogShell open={open} onOpenChange={setOpen} title="Refer a Buyer">
        <div className="space-y-3">
          {/* Typed on purpose: the Realtor is an external seat, so no tenant contact search here (156). */}
          <div><Label>Buyer Name *</Label><Input value={form.buyer_name} onChange={(e) => setForm({...form, buyer_name: e.target.value})} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Email</Label><Input type="email" value={form.buyer_email} onChange={(e) => setForm({...form, buyer_email: e.target.value})} className="mt-1" /></div>
            <div><Label>Phone</Label><PhoneInput value={form.buyer_phone} onChange={(d) => setForm({...form, buyer_phone: d})} className="mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Loan Type</Label>
              <Select value={form.loan_type} onValueChange={(v) => setForm({...form, loan_type: v})}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{optionsFrom(LOAN_TYPE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Est. Amount</Label><MoneyInput value={form.estimated_amount} onChange={(n) => setForm({...form, estimated_amount: n})} className="mt-1" /></div>
          </div>
          <div><Label>Property Address</Label><AddressFields value={form.property_address_parts} onChange={(a) => setForm({...form, property_address_parts: a})} testIdPrefix="referral-address" /></div>
          <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="mt-1" /></div>
          <Button variant="accent" className="w-full justify-center" onClick={submit} disabled={busy}>{busy ? <><Loader2 size={16} className="animate-spin" /> Submitting...</> : 'Send Referral'}</Button>
        </div>
      </DialogShell>
    </div>
  );
}
